// The products a person confirmed for the second PLL scene (pll-s02e01-2142-2200: S02E01, 21:42-22:00,
// Imogen at her mother's grave, a flashback, then Dr. Sullivan). Tried out on the local board only
// (Felipe, 2026-10-22). The clip is the last 18 s of the 21:30-22:00 scene: Felipe asked to "cut from
// second 0 to 12 ... leave 12 to 30", which drops the Orpheum beat and the wide cemetery shot, so the
// frame numbers here are the full scene's minus 288.
import type { PublishedClip, PublishedProduct } from '../../utils/clipApi';
import { curateWith, type ConfirmedProduct } from '../confirmed';

const confirmed = (product: Omit<PublishedProduct, 'gallery_row' | 'image' | 'score' | 'listings' | 'match_type'> & Partial<PublishedProduct>): PublishedProduct => ({
  gallery_row: -1,
  image: null,
  score: null,
  listings: null,
  match_type: 'Confirmed match',
  ...product,
});

/** Imogen's yellow floral collared sundress: WornOnTV (2x08, the same costume) names it a vintage 70s
 *  piece altered for the production, sold on Etsy and gone. Listed as the garment it is (WornOnTV's
 *  photo), with a similar vintage dress that IS on sale under "See similar items". */
export const IMOGEN_YELLOW_DRESS = confirmed({
  brand: 'Vintage (1970s)',
  name: 'Yellow floral collared sundress, altered for the production',
  color: 'Yellow floral',
  category: 'Apparel & Accessories > Clothing > Dresses',
  merchant: 'Etsy',
  image_url: '/shows/pll/imogen-yellow-collared-dress.jpg',
  price: 'Sold',
  url: 'https://wornontv.net/445875/',
});

export const SIMILAR_YELLOW_DRESS = confirmed({
  match_type: 'Similar style',
  brand: 'AtomicRevivalCo (vintage 1970s)',
  name: 'Sunshine Yellow Maxi Dress, floral embroidered white collar',
  color: 'Yellow',
  category: 'Apparel & Accessories > Clothing > Dresses',
  merchant: 'Etsy',
  image_url: '/shows/pll/similar-yellow-collared-dress.jpg',
  price: '$130.56',
  links: [{ marketplace: 'Etsy', url: 'https://www.etsy.com/listing/4450247539/vintage-70s-sunshine-yellow-maxi-dress', price: '$130.56', size: 'S' }],
});

/** Tabby's blouse in the bedroom flashback: a period piece, in no catalogue and on no WornOnTV post,
 *  and the demo gallery's nearest offers were rompers and skirts (Felipe: "lets also improve the match
 *  of the shirt the lady on the left wears when sitting in bed"). This is a SIMILAR STYLE on sale: a
 *  cream small-floral long-sleeve button-up, like the cardigan in the S02E07 scene. */
export const SIMILAR_FLORAL_BLOUSE = confirmed({
  match_type: 'Similar style',
  brand: 'Universal Thread',
  name: 'Long Sleeve Scalloped V-Neck Button-Up Blouse',
  color: 'Cream Floral',
  category: 'Apparel & Accessories > Clothing > Shirts & Tops',
  merchant: 'Target',
  image_url: '/shows/pll/similar-floral-blouse-cream.jpg',
  price: '$30.00',
  links: [
    {
      marketplace: 'Target',
      url: 'https://www.target.com/p/women-s-long-sleeve-scalloped-v-neck-button-up-blouse-universal-thread/-/A-95213750',
      price: '$30.00',
      size: 'XS-XXL',
    },
  ],
});

export const PLL_S02E01_CONFIRMED: ConfirmedProduct[] = [
  {
    // Measured on the published 2026-10-22 21:18Z run of the cut (17 tracks, no engulf/unify, detector
    // at 0.15 so Tabby's blouse in the flashback is seen at all). The woman beside Tabby in the
    // flashback is NOT wearing this dress (Felipe: "the lady on the right has a different dress than
    // before"), so that shot is not in this list; it keeps the catalog match curate published for it.
    product: IMOGEN_YELLOW_DRESS,
    similar: [SIMILAR_YELLOW_DRESS],
    garments: [
      { frames: [[7, 45]], at: [0.08, 0.67] }, // her sleeve, laying the flowers
      { frames: [[46, 101]], at: [0.5, 0.8] }, // looking up
      { frames: [[206, 276]], at: [0.47, 0.78] }, // the grave, close
      { frames: [[333, 411]], at: [0.43, 0.7] }, // standing, wide
      { frames: [[412, 431]], at: [0.44, 0.59] }, // with Dr. Sullivan
    ],
  },
  {
    // Tabby on the bed in the flashback, the only shot she is in.
    product: SIMILAR_FLORAL_BLOUSE,
    garments: [{ frames: [[142, 205]], at: [0.49, 0.47] }],
  },
];

const curate = curateWith(PLL_S02E01_CONFIRMED);

export function curatePllS02E01Clip(clip: PublishedClip): PublishedClip {
  return curate(clip);
}
